/**
 * 購物車頁的優惠碼：套用、移除（2026-10-19）。
 *
 * 用 Ajax Cart API `/cart/update.js` 的 `discount` 參數（多組以逗號分隔，空字串＝全部移除）。
 * 套用後由 Shopify 計算折扣，這支只判斷優惠碼能不能用，成功就重新載入購物車頁，
 * 讓伺服器渲染的小計／折扣／合計更新，金額一律以 Shopify 為準。
 *
 * 重新載入前會把表單上的訂單屬性（發票欄位、商品備註彙整）一起寫進購物車：
 * 發票欄位平常只在送出表單時才存，不帶的話客人填到一半就會被重新載入清掉。
 */
(() => {
  const root = document.querySelector('[data-cart-discount]');
  if (!root) return;

  const cartForm = root.closest('form');
  const input = root.querySelector('[data-cart-discount-input]');
  const applyButton = root.querySelector('[data-cart-discount-apply]');
  const status = root.querySelector('[data-cart-discount-status]');
  const updateUrl = `${root.dataset.cartUpdateUrl}.js`;
  const cartUrl = root.dataset.cartUrl;
  const messages = {
    applying: root.dataset.msgApplying,
    empty: root.dataset.msgEmpty,
    invalid: root.dataset.msgInvalid,
    duplicate: root.dataset.msgDuplicate,
    error: root.dataset.msgError,
  };
  let busy = false;

  /**
   * 目前已套用的優惠碼（伺服器渲染的標籤）。
   * @returns {string[]}
   */
  function appliedCodes() {
    return Array.from(root.querySelectorAll('[data-cart-discount-code]')).map((el) => el.dataset.cartDiscountCode);
  }

  /**
   * 顯示狀態訊息。
   * @param {string} message
   * @param {''|'error'} [state]
   */
  function setStatus(message, state = '') {
    status.textContent = message;
    status.dataset.state = state;
  }

  /**
   * 收集表單上的訂單屬性（name="attributes[...]"，停用的欄位瀏覽器本來就不會送出）。
   * @returns {Record<string, string>}
   */
  function formAttributes() {
    if (!cartForm) return {};
    const attributes = {};
    new FormData(cartForm).forEach((value, key) => {
      const match = key.match(/^attributes\[(.+)\]$/);
      if (match && typeof value === 'string') attributes[match[1]] = value;
    });
    return attributes;
  }

  /**
   * 以指定的優惠碼清單更新購物車，回傳 Shopify 的購物車 JSON。
   * @param {string[]} codes
   * @returns {Promise<object>}
   */
  async function updateDiscounts(codes) {
    const response = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), attributes: formAttributes() }),
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    return response.json();
  }

  /**
   * 判斷優惠碼是否實際套用到購物車。
   * 優先看 `discount_codes[].applicable`；沒有這個欄位時，改找整單與單品折扣的名稱。
   * @param {object} cart
   * @param {string} code
   * @returns {boolean}
   */
  function isApplied(cart, code) {
    const same = (value) => String(value || '').toLowerCase() === code.toLowerCase();
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.some((entry) => same(entry.code) && entry.applicable);
    }
    const cartLevel = (cart.cart_level_discount_applications || []).some((d) => same(d.title));
    const lineLevel = (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some((a) => same(a.discount_application?.title)),
    );
    return cartLevel || lineLevel;
  }

  /**
   * 鎖定／解除按鈕，避免連點重複送出。
   * @param {boolean} value
   */
  function setBusy(value) {
    busy = value;
    applyButton.disabled = value;
    root.querySelectorAll('[data-cart-discount-remove]').forEach((button) => {
      button.disabled = value;
    });
  }

  /** 套用輸入框中的優惠碼。 */
  async function apply() {
    if (busy) return;
    const code = input.value.trim();
    if (!code) {
      setStatus(messages.empty, 'error');
      input.focus();
      return;
    }

    const current = appliedCodes();
    if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
      setStatus(messages.duplicate, 'error');
      return;
    }

    setBusy(true);
    setStatus(messages.applying);
    try {
      const cart = await updateDiscounts([...current, code]);
      if (isApplied(cart, code)) {
        window.location.assign(cartUrl);
        return;
      }
      // 無效或不符合條件的優惠碼仍會留在購物車上，要拿掉，否則結帳頁會再顯示一次錯誤
      await updateDiscounts(current);
      setStatus(messages.invalid, 'error');
    } catch (error) {
      console.error('Unable to apply discount code.', error);
      setStatus(messages.error, 'error');
    }
    setBusy(false);
  }

  /**
   * 移除一組已套用的優惠碼。
   * @param {string} code
   */
  async function remove(code) {
    if (busy) return;
    setBusy(true);
    try {
      await updateDiscounts(appliedCodes().filter((c) => c !== code));
      window.location.assign(cartUrl);
    } catch (error) {
      console.error('Unable to remove discount code.', error);
      setStatus(messages.error, 'error');
      setBusy(false);
    }
  }

  applyButton.addEventListener('click', apply);

  // 輸入框在購物車表單裡，按 Enter 會送出整張表單，改成套用優惠碼
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    apply();
  });

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-discount-remove]');
    if (!button) return;
    remove(button.closest('[data-cart-discount-code]').dataset.cartDiscountCode);
  });
})();
